<template>
  <div
    class="card-viewer"
    :class="{
      'card-viewer-image-only': showImage && !showInfo,
      'card-viewer-details-only': showInfo && !showImage,
      'card-viewer-fluid': fluid,
      'card-viewer-row': layout === 'row',
    }"
    :style="boxStyle"
  >
    <div
      v-if="showImage"
      class="card-viewer-image"
      :style="imageBoxStyle"
    >
      <CardArt
        v-if="card"
        :card-id="card.id"
        :foil="card.foil"
        :face-down="faceDown || imgError || !imageUrl"
        :alt="display?.name || 'Card'"
        intensity="full"
        @error="imgError = true"
      />
      <img
        v-else
        :src="sleeveSrc"
        alt="Card back"
      />
    </div>
    <div
      v-if="showInfo && !display"
      class="card-viewer-placeholder"
    >
      {{ emptyHint || t('duel.viewerEmpty') }}
    </div>
    <div
      v-else-if="showInfo && faceDown"
      class="card-viewer-placeholder"
    >
      {{ t('duel.viewerFaceDown') }}
    </div>
    <div
      v-else-if="display && !faceDown && showInfo"
      class="card-viewer-detail"
      :style="detailStyle"
    >
      <div class="card-viewer-header">
        <div class="card-viewer-name" :class="{ 'fusion-ready': fusionReady }">
          {{ display.name }}
        </div>
        <div v-if="foilLabel" class="card-viewer-foil">{{ foilLabel }}</div>
        <div
          v-if="showFoilSelect"
          class="card-viewer-foil-pick"
          @pointerdown.stop
        >
          <label class="visually-hidden" :for="foilSelectId">
            {{ t('collection.preferredFoil') }}
          </label>
          <select
            :id="foilSelectId"
            class="md-field foil-select"
            :value="card?.foil"
            @change="onFoilChange"
          >
            <option
              v-for="id in foilChoices"
              :key="id"
              :value="id"
            >
              {{ t(`packs.rarity.${id}`) }}
            </option>
          </select>
        </div>
        <div v-if="display.level" class="card-viewer-level">
          <span v-for="n in display.level" :key="n">&#9733;</span>
        </div>
      </div>
      <div class="card-viewer-meta">
        <span v-if="kindLine" class="card-viewer-typeline">
          {{ kindLine }}
        </span>
        <span v-if="display.atk != null" class="card-viewer-stats">
          <span class="stat atk" :class="{ boosted: atkDelta > 0, reduced: atkDelta < 0 }">
            ATK {{ display.atk }}<span v-if="atkDelta !== 0" class="delta">({{ atkDelta > 0 ? '+' : '' }}{{ atkDelta }})</span>
          </span>
          <span class="stat-sep">/</span>
          <span class="stat def" :class="{ boosted: defDelta > 0, reduced: defDelta < 0 }">
            DEF {{ display.def }}<span v-if="defDelta !== 0" class="delta">({{ defDelta > 0 ? '+' : '' }}{{ defDelta }})</span>
          </span>
        </span>
      </div>
      <div
        v-if="display.desc"
        class="card-viewer-desc"
        :class="{ 'is-marked': fusionParts }"
      >
        <template v-if="fusionParts">
          <span
            v-for="(part, i) in fusionParts"
            :key="i"
            :class="part.state === 'plain' ? undefined : `material-${part.state}`"
          >{{ part.text }}</span>
        </template>
        <template v-else>{{ display.desc }}</template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { asCardFoil, type CardFoilId } from '@shared/index'
import { getLocalCardImageUrl, currentSleeveUrl } from '../utils/cardImages'
import { localizeCard, localizedNameFromEnglish, spellTrapKindLabel } from '../i18n/localizeCard'
import {
  fusionMaterialsReady,
  splitFusionText,
} from '../game/fusionMaterialText'
import { useI18n } from '../i18n/t'
import CardArt from './CardArt.vue'
import { getCatalogCard } from '../game/packs/packCatalog'

export interface CardViewerData {
  id: string
  name: string
  type: string
  level?: number
  typeline?: string[]
  race?: string
  attribute?: string
  atk?: number
  def?: number
  /** original printed stats, when the shown atk/def are modified by an effect. */
  baseAtk?: number
  baseDef?: number
  desc?: string
  card_images?: { image_url: string }[]
  foil?: CardFoilId | string | null
}

const props = withDefaults(defineProps<{
  card: CardViewerData | null
  faceDown?: boolean
  width?: number
  showInfo?: boolean
  /** When false, only the text block is rendered (compact duel right rail). */
  showImage?: boolean
  /**
   * Skip fixed pixel size: the parent CSS sizes the art (compact left rail).
   */
  fluid?: boolean
  /** Stack art over text (default) or put art left and text right. */
  layout?: 'column' | 'row'
  /** When two or more prints are owned, the viewer can switch among these. */
  foilChoices?: CardFoilId[]
  /** Replaces the empty-slot copy when there is no card. */
  emptyHint?: string
  /** Face-down back. Falls back to the local sleeve when omitted. */
  sleeveUrl?: string
  /**
   * English catalog names the player can use as fusion materials.
   * Omit it to leave fusion text uncolored.
   */
  materialNames?: string[]
}>(), {
  faceDown: false,
  width: 200,
  showInfo: false,
  showImage: true,
  fluid: false,
  layout: 'column',
  foilChoices: () => [],
  emptyHint: '',
})

const emit = defineEmits<{
  'update:foil': [foil: CardFoilId]
}>()

const { t } = useI18n()

const sleeveSrc = computed(() => props.sleeveUrl || currentSleeveUrl())

const display = computed(() => {
  if (!props.card) return null
  const catalog = getCatalogCard(String(props.card.id))
  return localizeCard({
    ...props.card,
    type: props.card.type || catalog?.type || '',
    race: props.card.race || catalog?.race,
    attribute: props.card.attribute || catalog?.attribute,
    typeline:
      props.card.typeline && props.card.typeline.length > 0
        ? props.card.typeline
        : catalog?.typeline,
  })
})

const fusionParts = computed(() => {
  const names = props.materialNames
  const card = props.card
  if (!names || !card || props.faceDown) return null
  const fusion =
    card.typeline?.includes('Fusion')
    || card.type.toLowerCase().includes('fusion')
  const desc = display.value?.desc
  if (!fusion || !desc) return null
  return splitFusionText(desc, names.map((name) => localizedNameFromEnglish(name)))
})

const fusionReady = computed(() =>
  fusionParts.value ? fusionMaterialsReady(fusionParts.value) : false,
)

const kindLine = computed(() => {
  const card = props.card
  const shown = display.value
  if (!card || !shown) return ''
  const catalog = getCatalogCard(String(card.id))
  const type = card.type || catalog?.type || ''
  const race = card.race || catalog?.race
  const spellTrap = spellTrapKindLabel(type, race)
  if (spellTrap) return spellTrap
  const parts: string[] = []
  if (shown.attribute) parts.push(shown.attribute)
  if (Array.isArray(shown.typeline) && shown.typeline.length > 0) {
    parts.push(...shown.typeline)
  } else if (shown.type) {
    parts.push(shown.type)
  }
  return parts.join(' / ')
})

const imgError = ref(false)

watch(() => props.card, () => {
  imgError.value = false
})

const imageHeight = computed(() => Math.round(props.width * 1.46))

const isRow = computed(() => props.layout === 'row')

const boxStyle = computed((): Record<string, string> => {
  const art = `${props.width}px`
  if (props.fluid || isRow.value) {
    return { '--viewer-art-width': art }
  }
  return { width: `${props.width}px`, '--viewer-art-width': art }
})

const imageBoxStyle = computed(() => {
  if (props.fluid || isRow.value) return undefined
  return { width: `${props.width}px`, height: `${imageHeight.value}px` }
})

const detailStyle = computed(() => {
  if (props.fluid || isRow.value) return undefined
  return { width: `${props.width}px` }
})

const showFoilSelect = computed(() => (props.foilChoices?.length ?? 0) >= 2)

const foilLabel = computed(() => {
  if (props.faceDown || !props.card?.foil) return ''
  return t(`packs.rarity.${asCardFoil(props.card.foil)}`)
})

const foilSelectId = computed(
  () => `foil-select-${props.card?.id ?? 'none'}`,
)

function onFoilChange(event: Event): void {
  const value = (event.target as HTMLSelectElement).value
  emit('update:foil', asCardFoil(value))
}

// Difference between the current and printed stats (from equips, field spells, etc.).
const atkDelta = computed(() => {
  const c = props.card
  if (!c || c.atk == null || c.baseAtk == null) return 0
  return c.atk - c.baseAtk
})
const defDelta = computed(() => {
  const c = props.card
  if (!c || c.def == null || c.baseDef == null) return 0
  return c.def - c.baseDef
})

const imageUrl = computed(() => {
  if (imgError.value) return ''
  const cardId = props.card?.id
  if (!cardId) return props.card?.card_images?.[0]?.image_url || ''
  return getLocalCardImageUrl(cardId)
})
</script>

<style scoped>
.card-viewer {
  display: flex;
  flex-direction: column;
  gap: 6px;
  pointer-events: none;
  user-select: none;
  height: 100%;
}

.card-viewer-foil {
  color: var(--md-sys-color-primary, #e8c547);
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.02em;
}

.card-viewer-foil-pick {
  pointer-events: auto;
  margin-top: 4px;
}

.foil-select {
  width: 100%;
  cursor: pointer;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.card-viewer-image {
  border-radius: 12px;
  overflow: hidden;
  clip-path: inset(0 round 12px);
  box-shadow:
    0 1px 2px rgba(0, 0, 0, 0.3),
    0 1px 3px 1px rgba(0, 0, 0, 0.15);
  background: var(--md-sys-color-surface-container, #1f1f23);
}

.card-viewer-image img,
.card-viewer-image :deep(.card-art) {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
  border-radius: 12px;
}

.card-viewer-detail {
  background: var(--md-sys-color-surface-container, #1f1f23);
  border: 1px solid var(--md-sys-color-outline-variant, #46464f);
  border-radius: 12px;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  box-sizing: border-box;
  flex: 1;
  min-height: 0;
  font-family: Roboto, system-ui, sans-serif;
}

.card-viewer-header {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.card-viewer-name {
  font-weight: 400;
  color: var(--md-sys-color-on-surface, #e5e1e6);
  font-size: 22px;
  line-height: 28px;
}

.card-viewer-name.fusion-ready {
  color: light-dark(#166534, #4ade80);
}

.card-viewer-level {
  color: var(--md-sys-color-primary, #e8c547);
  font-size: 14px;
  letter-spacing: -1px;
}

.card-viewer-meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 15px;
}

.card-viewer-typeline {
  color: var(--md-sys-color-on-surface-variant, #c6c5d0);
}

.card-viewer-stats {
  font-size: 16px;
}

.stat.atk {
  color: #f87171;
}

.stat-sep {
  color: #6b7280;
  margin: 0 3px;
}

.stat.def {
  color: #60a5fa;
}

/* When a stat is changed by an effect, override its base color to signal it. */
.stat.boosted {
  color: #7cfc00;
}

.stat.reduced {
  color: #f87171;
}

.delta {
  font-size: 0.8em;
  margin-left: 3px;
  opacity: 0.9;
}

.card-viewer-desc {
  --fusion-size: 14px;
  --fusion-leading: 20px;
  color: var(--md-sys-color-on-surface-variant, #c6c5d0);
  font-size: var(--fusion-size);
  line-height: var(--fusion-leading);
  margin-top: 4px;
  border-top: 1px solid var(--md-sys-color-outline-variant, #46464f);
  padding-top: 8px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
  pointer-events: auto;
}

.card-viewer-desc.is-marked {
  font-size: 0;
  line-height: 0;
}

.card-viewer-desc.is-marked span {
  font-size: var(--fusion-size);
  line-height: var(--fusion-leading);
}

.material-available {
  color: light-dark(#166534, #4ade80);
  font-weight: 700;
}

.material-missing {
  color: light-dark(#b3261e, #f87171);
}

.card-viewer-desc::-webkit-scrollbar {
  width: 3px;
}

.card-viewer-desc::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 2px;
}

.card-viewer-desc::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
}

.card-viewer-placeholder {
  flex: 1;
  min-height: 0;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid var(--md-sys-color-outline-variant, #46464f);
  background: var(--md-sys-color-surface-container, #1f1f23);
  color: #94a3b8;
  font-size: 13px;
  line-height: 18px;
  box-sizing: border-box;
}

.card-viewer-details-only {
  height: 100%;
}

.card-viewer-details-only .card-viewer-detail,
.card-viewer-details-only .card-viewer-placeholder {
  width: 100% !important;
}

.card-viewer-fluid.card-viewer-image-only {
  width: 100%;
  height: 100%;
}

.card-viewer-fluid.card-viewer-image-only .card-viewer-image {
  width: 100%;
  height: 100%;
}

.card-viewer-row {
  flex-direction: row;
  align-items: stretch;
  gap: 12px;
  width: 100%;
  height: 100%;
}

.card-viewer-row .card-viewer-image {
  flex: 0 0 var(--viewer-art-width, 128px);
  width: var(--viewer-art-width, 128px);
  height: calc(var(--viewer-art-width, 128px) * 1.46);
  max-height: 100%;
  align-self: flex-start;
}

.card-viewer-row .card-viewer-detail,
.card-viewer-row .card-viewer-placeholder {
  flex: 1 1 0;
  width: auto;
  min-width: 0;
  min-height: 0;
  height: 100%;
  overflow: hidden;
}

.card-viewer-row .card-viewer-name {
  font-size: 18px;
  line-height: 22px;
}

.card-viewer-row .card-viewer-desc {
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}
</style>
